/**
 * Completion rows carry the run that wrote them (`runId`) and the server time
 * that run started (`runAt`). OpenAIArticleGenerate deletes the rows of
 * earlier runs, so this only drops rows a late job of an earlier run wrote
 * back. Rows without `runAt` are from before run ids (v1.14.2 and older);
 * they count only while no newer run exists.
 */

type RunRow = {runId?: unknown; runAt?: unknown} | null | undefined;

function runAtOf(row: RunRow): number {
    const value = Number(row?.runAt);
    return Number.isFinite(value) && value > 0 ? value : 0;
}

/** The run start of the newest run in the map, or 0 when no row has one. */
export function latestRunAt(map: Record<string, RunRow[] | null | undefined>): number {
    let latest = 0;
    for (const rows of Object.values(map || {})) {
        for (const row of rows || []) {
            latest = Math.max(latest, runAtOf(row));
        }
    }
    return latest;
}

/** Keep only the rows of the newest run. A map without run stamps is unchanged. */
export function currentRunCompletions<T extends RunRow>(
    map: Record<string, T[] | null | undefined>,
): Record<string, T[]> {
    const latest = latestRunAt(map);
    const out: Record<string, T[]> = {};
    for (const [part, rows] of Object.entries(map || {})) {
        out[part] = latest ? (rows || []).filter((row) => runAtOf(row) === latest) : [...(rows || [])];
    }
    return out;
}

/** Rows of one part document's run. A part document without `runId` keeps every row. */
export function rowsOfPartRun<T extends RunRow>(rows: T[] | null | undefined, partRunId: unknown): T[] {
    if (typeof partRunId !== 'string' || !partRunId) {
        return [...(rows || [])];
    }
    return (rows || []).filter((row) => row?.runId === partRunId);
}
